import { useCallback, useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { analyticsApi } from '../services/analytics-api.service';
import type {
  ReferralListDirection,
  ReferralListQueryParams,
} from '../types/engagement-metrics.types';

const DEBOUNCE_MS = 300;
export const REFERRAL_LIST_PAGE_SIZE = 50;

/**
 * Query keys for the Referrals tab. Everything sits under ['analytics'], so
 * the manual-referral create mutation (and edit/delete, slices 19/20) refetch
 * the current page from the same cursor by invalidating ['analytics'] or
 * `referralListKeys.all`.
 */
export const referralListKeys = {
  all: ['analytics', 'engagement-metrics', 'referrals'] as const,
  page: (params: ReferralListQueryParams) => [...referralListKeys.all, 'page', params] as const,
};

export type ReferralListFilters = {
  search: string;
  billable: 'ALL' | 'BILLABLE' | 'NON_BILLABLE';
  directions: ReferralListDirection[];
  origin: '' | 'PLATFORM' | 'MANUAL';
  careSeeker: '' | 'WITH' | 'WITHOUT';
  referralDateFrom: string;
  referralDateTo: string;
  createdDateFrom: string;
  createdDateTo: string;
};

export type ReferralListSort = {
  sortBy: 'referralDate' | 'createdDate';
  sortDir: 'asc' | 'desc';
};

export const EMPTY_REFERRAL_FILTERS: ReferralListFilters = {
  search: '',
  billable: 'ALL',
  directions: [],
  origin: '',
  careSeeker: '',
  referralDateFrom: '',
  referralDateTo: '',
  createdDateFrom: '',
  createdDateTo: '',
};

/** Filters inside the collapsible panel (search and Billable live in the toolbar). */
export const panelFilterCount = (f: ReferralListFilters) =>
  [
    f.directions.length > 0,
    f.origin,
    f.careSeeker,
    f.referralDateFrom || f.referralDateTo,
    f.createdDateFrom || f.createdDateTo,
  ].filter(Boolean).length;

/** Builds the request params (without cursor). Also the export params for slice 21. */
export const toReferralListParams = (
  filters: ReferralListFilters,
  search: string,
  sort: ReferralListSort,
): ReferralListQueryParams => {
  const params: ReferralListQueryParams = { limit: REFERRAL_LIST_PAGE_SIZE };
  if (search.trim()) params.search = search.trim();
  if (filters.billable !== 'ALL') params.isBillable = filters.billable === 'BILLABLE';
  if (filters.directions.length) params.direction = filters.directions.join(',');
  if (filters.origin) params.origin = filters.origin;
  if (filters.careSeeker) params.careSeeker = filters.careSeeker;
  if (filters.referralDateFrom) params.referralDateFrom = filters.referralDateFrom;
  if (filters.referralDateTo) params.referralDateTo = filters.referralDateTo;
  if (filters.createdDateFrom) params.createdDateFrom = filters.createdDateFrom;
  if (filters.createdDateTo) params.createdDateTo = filters.createdDateTo;
  params.sortBy = sort.sortBy;
  params.sortDir = sort.sortDir;
  return params;
};

/**
 * Referrals tab state: filters (local state, no URL params), sort, and a
 * cursor stack for Previous / Next. Any filter, search or sort change resets
 * to the first page.
 */
export const useReferralList = (enabled = true) => {
  const [filters, setFilters] = useState<ReferralListFilters>(EMPTY_REFERRAL_FILTERS);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sort, setSort] = useState<ReferralListSort>({ sortBy: 'referralDate', sortDir: 'desc' });
  // cursors[i] = cursor that loads page i; page 0 has none. Tagged with the
  // params they belong to, so a filter/search/sort change starts at page 0.
  const [paging, setPaging] = useState<{ key: string; cursors: (string | undefined)[] }>({
    key: '',
    cursors: [undefined],
  });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filters.search]);

  const params = useMemo(
    () => toReferralListParams(filters, debouncedSearch, sort),
    [filters, debouncedSearch, sort],
  );

  const paramsKey = JSON.stringify(params);
  const cursors = paging.key === paramsKey ? paging.cursors : [undefined];
  const setCursors = (next: (string | undefined)[]) => setPaging({ key: paramsKey, cursors: next });

  const pageIndex = cursors.length - 1;
  const cursor = cursors[pageIndex];
  const pageParams = useMemo(() => (cursor ? { ...params, cursor } : params), [params, cursor]);

  const query = useQuery({
    queryKey: referralListKeys.page(pageParams),
    queryFn: () => analyticsApi.getReferralList(pageParams),
    placeholderData: keepPreviousData,
    enabled,
  });

  const updateFilters = useCallback(
    (patch: Partial<ReferralListFilters>) => setFilters((current) => ({ ...current, ...patch })),
    [],
  );

  const toggleSort = useCallback(
    (sortBy: ReferralListSort['sortBy']) =>
      setSort((current) =>
        current.sortBy === sortBy
          ? { sortBy, sortDir: current.sortDir === 'desc' ? 'asc' : 'desc' }
          : { sortBy, sortDir: 'desc' },
      ),
    [],
  );

  const nextCursor = query.data?.nextCursor ?? null;

  return {
    rows: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    pageIndex,
    params,
    filters,
    updateFilters,
    clearFilters: () => setFilters(EMPTY_REFERRAL_FILTERS),
    sort,
    toggleSort,
    hasPrevious: pageIndex > 0,
    hasNext: Boolean(nextCursor),
    goNext: () => {
      if (nextCursor) setCursors([...cursors, nextCursor]);
    },
    goPrevious: () => {
      if (cursors.length > 1) setCursors(cursors.slice(0, -1));
    },
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
};
